import { useState } from "react";
import { motion } from "framer-motion";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import type { ProductHighlight } from "../data/products";

interface HighlightCardProps {
  highlight: ProductHighlight;
  expanded: boolean;
  onToggle: () => void;
}

function HighlightCard({ highlight, expanded, onToggle }: HighlightCardProps) {
  const reducedMotion = usePrefersReducedMotion();
  return (
    <div className="h-full rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5">
      <button type="button" onClick={onToggle} aria-expanded={expanded} className="w-full text-left">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-400/15 text-teal-500">
          <highlight.icon className="h-5 w-5" strokeWidth={1.5} />
        </div>
        <h4 className="mt-4 font-display font-semibold text-ocean-950">{highlight.title}</h4>
        <p className="mt-1.5 text-sm leading-relaxed text-ocean-800/70">{highlight.description}</p>
      </button>
      {expanded && (
        <motion.div
          initial={reducedMotion ? false : { opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          transition={{ duration: reducedMotion ? 0 : 0.25 }}
          className="overflow-hidden"
        >
          {highlight.media?.type === "image" && (
            <img
              src={highlight.media.src}
              alt={highlight.media.alt}
              title={highlight.media.credit}
              loading="lazy"
              className="mt-3 w-full rounded-lg object-cover"
            />
          )}
          <p className="mt-3 border-t border-teal-400/20 pt-3 text-xs leading-relaxed text-ocean-800/60">
            {highlight.expandedDetail}
          </p>
        </motion.div>
      )}
    </div>
  );
}

interface ProductHighlightCarouselProps {
  highlights: ProductHighlight[];
}

/** Grid at sm+; a native scroll-snap carousel (touch-swipeable, no library) below sm. */
export default function ProductHighlightCarousel({ highlights }: ProductHighlightCarouselProps) {
  const [expandedTitle, setExpandedTitle] = useState<string | null>(null);
  const toggle = (title: string) => setExpandedTitle((cur) => (cur === title ? null : title));

  return (
    <>
      <div className="hidden gap-5 sm:grid sm:grid-cols-2 lg:grid-cols-3">
        {highlights.map((h) => (
          <HighlightCard key={h.title} highlight={h} expanded={expandedTitle === h.title} onToggle={() => toggle(h.title)} />
        ))}
      </div>

      <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 sm:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {highlights.map((h) => (
          <div key={h.title} className="w-[82%] shrink-0 snap-start">
            <HighlightCard highlight={h} expanded={expandedTitle === h.title} onToggle={() => toggle(h.title)} />
          </div>
        ))}
      </div>
    </>
  );
}
